import { onMounted, ref, watchEffect } from 'vue';
import { useStatusPlugin } from '@panel/plugins/status';
import IncidentToastContent from '@panel/components/app/IncidentToastContent.vue';
import MaintenanceToastContent from '@panel/components/app/MaintenanceToastContent.vue';
import { type IncidentImpact } from '@panel/plugins/types';
import { GpToastPosition, type GpToastType, useGpToasts } from '@gportal/uikit';

export const DISMISSED_STATUS_LOCAL_STORAGE_KEY = 'gpcore-dismissed-status';

export default (
  { immediate, ignoreLocalDismissed }: { immediate: boolean; ignoreLocalDismissed: boolean } = {
    immediate: true,
    ignoreLocalDismissed: false,
  },
) => {
  const {
    currentMaintenance,
    activeMaintenances,
    loadMaintenances,
    currentIncident,
    incidents,
    loadIncidents,
  } = useStatusPlugin();

  const { createToast } = useGpToasts();
  const isDisplayingList = ref<Array<string>>([]);
  const dismissedList = ref<Array<string>>([]);
  const getLocalDismissedStatusList = () => {
    return JSON.parse(localStorage.getItem(DISMISSED_STATUS_LOCAL_STORAGE_KEY) ?? '[]');
  };
  const dismissStatus = (id: string) => {
    dismissedList.value.push(id);

    if (ignoreLocalDismissed) {
      return;
    }

    const dismissedStatusList = getLocalDismissedStatusList();
    dismissedStatusList.push(currentMaintenance.value?.id);
    localStorage.setItem(DISMISSED_STATUS_LOCAL_STORAGE_KEY, JSON.stringify(dismissedStatusList));
  };

  const getToastType = (impact: IncidentImpact): GpToastType => {
    switch (impact) {
      case 'none':
        return 'success';
      case 'maintenance':
        return 'primary';
      case 'minor':
        return 'warning';
      case 'major':
        return 'danger';
      case 'critical':
        return 'danger';
      default:
        return 'primary';
    }
  };

  const showCurrentMaintenanceToast = () => {
    let localDismissedList = [];
    if (!ignoreLocalDismissed) {
      localDismissedList = getLocalDismissedStatusList();
    }

    activeMaintenances.value.forEach((maintenance) => {
      // if there is a current maintenance and it hasn't been dismissed yet, show the toast
      if (
        maintenance != null &&
        !dismissedList.value.includes(maintenance.id) &&
        !localDismissedList.includes(maintenance.id) &&
        !isDisplayingList.value.includes(maintenance.id)
      ) {
        isDisplayingList.value.push(maintenance.id);
        createToast({
          bodyComponent: MaintenanceToastContent,
          bodyComponentProps: {
            maintenance,
          },
          position: 'bottom-right' as GpToastPosition,
          timeout: 0,
          toastStyle:
            /* tw */ 'tw-bg-gp-cyan tw-p-4 tw-flex tw-items-start tw-max-w !tw-text-black',
          onBeforeDismiss: () => {
            dismissStatus(maintenance.id);
          },
        });
      }
    });
  };

  const showCurrentIncidentsToast = () => {
    let localDismissedList = [];
    if (!ignoreLocalDismissed) {
      localDismissedList = getLocalDismissedStatusList();
    }

    incidents.value.forEach((incident) => {
      if (
        !dismissedList.value.includes(incident.id) &&
        !localDismissedList.includes(incident.id) &&
        !isDisplayingList.value.includes(incident.id)
      ) {
        isDisplayingList.value.push(incident.id);
        createToast({
          bodyComponent: IncidentToastContent,
          bodyComponentProps: {
            incident,
          },
          position: 'bottom-right',
          timeout: 0,
          pauseOnHover: false,
          toastClassName: /* tw */ `tw-p-4 tw-flex tw-items-start ${getToastType(incident.impact)}`,
          closeButtonClassName: /* tw */ 'tw-text-black tw-ml-4 !tw-opacity-100',
          icon: false,
          onClose: () => dismissStatus(incident.id),
        });
      }
    });
  };

  watchEffect(() => {
    showCurrentMaintenanceToast();
    showCurrentIncidentsToast();
  });

  onMounted(async () => {
    if (immediate) {
      await Promise.all([loadMaintenances(), loadIncidents()]);
      showCurrentMaintenanceToast();
      showCurrentIncidentsToast();
    }
  });

  return {
    activeMaintenances,
    currentMaintenance,
    loadMaintenances,
    incidents,
    currentIncident,
    loadIncidents,
    showCurrentStatusToast: showCurrentMaintenanceToast,
  };
};
